import { useSelector } from 'react-redux';
import { ROLES } from 'src/constants/auth';
import { getAuth } from 'src/redux/slices/auth';
import { Role } from 'src/schemas/Role';

import Permissions from './permissions';
import PERMISSIONS_BY_ROLE from './permissionsByRole';

export const resolvePermissionRole = ({
  currentUser,
  superAdminToken,
  role,
}: Pick<IAuthState, 'currentUser' | 'superAdminToken' | 'role'>): Role => {
  if (currentUser?.role?.id) {
    return currentUser.role.id;
  }

  if (superAdminToken) {
    return ROLES.SUPER_ADMIN;
  }

  return role;
};

export const canSeeActivity = (
  auth: Pick<IAuthState, 'currentUser' | 'superAdminToken' | 'role'>,
): boolean => resolvePermissionRole(auth) !== ROLES.FINANCE_MANAGER;

const usePermissions = () => {
  const auth = useSelector(getAuth);
  const { currentAccount } = auth;

  const hasPermission = (permission: Permissions): boolean => {
    const userRole = resolvePermissionRole(auth);

    return PERMISSIONS_BY_ROLE[userRole]?.includes(permission) ?? false;
  };

  const hasAccountPermission = (permission: string, account?: Account): boolean => {
    if (permission === 'contacts') {
      return !!currentAccount?.settings?.data?.contacts?.list?.value;
    }
    if (permission === 'deals') {
      const accountToUse = account || currentAccount;
      return !!accountToUse?.settings?.data?.dealer?.deals?.value;
    }

    if (permission === 'pricing') {
      const accountToUse = account || currentAccount;
      return !!accountToUse?.settings?.data?.dealer?.pricing?.value;
    }

    if (permission === 'notifications') {
      const accountToUse = account || currentAccount;
      return !!accountToUse?.settings?.data?.dealer?.notifications?.value;
    }

    return false;
  };

  return {
    hasPermission,
    hasAccountPermission,
    canSeeActivity: canSeeActivity(auth),
  };
};

export default usePermissions;
